import { FormItem, Input, Textarea } from '@vkontakte/vkui';
import { useState } from 'react';
import { t } from '../../i18n';
import { Action, FieldLabel, FormActions, ImageField, SectionTitle } from '../../shared/ui';

/**
 * Форма предмета — общая для «добавить» и «править».
 *
 * Отдельной панели у неё нет намеренно: в роутере под склад отведён один
 * экран, и открывать форму соседней панелью значило бы добавить шаг в историю,
 * из которого кнопка «назад» в ВК выводит не туда, куда ждёт игрок.
 *
 * Блоки идут так же, как у питомца и способности: сначала текст, потом фото,
 * потом действия. Заголовок первого блока не нужен — его говорит шапка группы
 * («Новый предмет», «Правка предмета»).
 */

export interface ItemFormValues {
  title: string;
  description: string;
  imageId: string | null;
}

export const EMPTY_ITEM: ItemFormValues = {
  title: '',
  description: '',
  imageId: null,
};

export interface ItemFormProps {
  initial: ItemFormValues;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: ItemFormValues) => void;
  onCancel: () => void;
}

export function ItemForm({ initial, submitLabel, busy, onSubmit, onCancel }: ItemFormProps) {
  const [values, setValues] = useState<ItemFormValues>(initial);
  const patch = (next: Partial<ItemFormValues>) => setValues((prev) => ({ ...prev, ...next }));

  // Название — единственное, без чего сервер откажет: колонка title обязательна.
  const blocker = values.title.trim().length === 0 ? t('inventory.form.blocker') : null;

  const canSubmit = blocker === null && busy !== true;

  return (
    <div>
      <SectionTitle>{t('inventory.form.section.about')}</SectionTitle>

      <FormItem top={<FieldLabel required>{t('common.field.title')}</FieldLabel>}>
        <Input
          value={values.title}
          placeholder={t('inventory.form.title.placeholder')}
          disabled={busy}
          onChange={(event) => patch({ title: event.target.value })}
        />
      </FormItem>

      <FormItem top={<FieldLabel>{t('common.field.description')}</FieldLabel>}>
        <Textarea
          rows={3}
          value={values.description}
          disabled={busy}
          onChange={(event) => patch({ description: event.target.value })}
        />
      </FormItem>

      <SectionTitle>{t('common.field.image')}</SectionTitle>

      {/* Подпись у поля своя, вторым заголовком сверху её не дублируем. */}
      <FormItem top={<FieldLabel>{t('inventory.form.image.label')}</FieldLabel>}>
        <ImageField
          label={t('inventory.form.image.button')}
          hint={t('inventory.form.image.hint')}
          imageId={values.imageId}
          onChange={(imageId) => patch({ imageId })}
          disabled={busy}
        />
      </FormItem>

      {/* В строку, как в макете: главное действие тянется, отказ от него
          стоит по содержимому. */}
      <FormActions>
        <Action
          tone="action"
          size="l"
          stretched
          loading={busy}
          disabled={!canSubmit}
          onClick={() => onSubmit(values)}
        >
          {submitLabel}
        </Action>
        <Action tone="quiet" size="l" stretched disabled={busy} onClick={onCancel}>
          {t('common.action.cancel')}
        </Action>
      </FormActions>
    </div>
  );
}
